import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as PIXI from 'pixi.js';
import type { DestroyOptions, MediaSize } from '../../types';
import { AspectRatio } from '../../aspectRatioConfig';
import { FETCH_ON_BUFFER } from '../../types';
import { AudioAsset } from '../../audio/AudioAsset';
import { maybeProxy } from '../../libs/other';
import { TransformMatrix } from '../../libs/TransformMatrix';
import { getMidPointFromRect } from '../../libs/padding';
import { BufferingAbortedError, FailedToLoadError } from '../../errors';

import {
    assertInArray,
    assertNumber,
    assertInEnum,
    assertMinimum,
    assertIsValidMediaSourceData,
} from '../../validators';
import { Config } from '../../video-data/config';
import { LinearCurve } from '../../libs/LinearCurve';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import type { LicenseEnum, SeekMode } from '../../enums';
import {
    AssetCreatedFrom,
    BufferingMode,
    DynamicStylingCreationLevel,
    CreationType,
} from '../../enums';
import { BooleanAttribute, getAttributesToSerialize } from '../attributes';
import { AdjustmentFilter, BlurFilter, HslAdjustmentFilter, OldFilmFilter } from '../filters';
import { StencilMask } from '../mask/StencilMask';
import type { FillMode } from '../../video-data/types';
import type { CompositingAsset } from '../CompositingAsset';
import { ErrorReporter } from '../../libs/error-reporter';
import { PlaybackRegions, type PlaybackRegion } from '../PlaybackRegions';
import { ColorGradingSettings } from '../ColorGradingSettings';
import { roundToPrecision } from '../../libs/frameUtils';
import { isPromiseLike } from '../../common';
import { WebpResource } from '../resources/WebpResource';
import { DynamicStylingMixin } from '../dynamicStylingMixin';
import { MorphManager, type MorphLinkedPropertySpecs } from '../morph/MorphManager';
import { calculateScaledResolution, resetTransform } from './mediaUtils';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';
import type { TimeAwareMediaAsset } from './TimeAwareMediaAsset';
import { equalWithinEpsilon, resetSprite } from './utils';
import { ColorGradingManager } from './ColorGradingManager';

type MediaAssetBase64DataType = {
    base64: string;
    width: number;
    height: number;
    format: string;
};

class MediaAssetBase64Data {
    base64: string;
    width: number;
    height: number;
    format: string;

    constructor({ base64, width, height, format }: MediaAssetBase64DataType) {
        this.base64 = base64;
        this.width = width;
        this.height = height;
        this.format = format;
    }

    /** strips away the data URL prefix from the base 64 string. This is necessary if we want to directly write it to disk. */
    withoutDataURLPrefix() {
        return this.base64.replace(`data:${this.format};base64,`, '');
    }
}

type TranslationLimit = {
    /**
     *  txLimit - max value of transformMatrix.tx
     */
    x: [number, number];
    /**
     * tyLimit - max value of transformMatrix.ty
     */
    y: [number, number];
};

export type MediaAssetSettings = BaseAssetSettings & {
    fillMode?: FillMode;
    audioMode: string;
    seekWaitTime: number;
    mediaSizeToUse: MediaSize;
};

export type MediaSourceData = {
    uuid: string;
    type: string;
    source: number;
    source_id: string;
    license: LicenseEnum;
    is_editorial: boolean;
    width: number;
    height: number;
    thumbnail_url: string;
    preview_url: string;
    render_url: string | null; // allowed to be null for Shutterstock, Getty, user uploads
    title: string | null;
    // properties only on VideoAssets
    poster_url?: string;
    duration?: number;
    has_audio?: boolean | null;
    has_voiceover?: boolean | null;
    ai_avatar?: number | null;
    audio_url?: string | null;
};

function isVideoElementLoaded(videoElement: HTMLVideoElement) {
    return !Number.isNaN(videoElement.duration) && videoElement.duration !== undefined;
}

/**
 * A MediaAsset Object will load the specified media file. This can be either an image or video.
 *
 * EVENTS MEDIA ASSET SENDS
 * - assetCanPlay:  MediaAsset is now playable.  Typically this means that the asset has loaded but also
 *                  may be sent even if the asset hasn't loaded yet.  This behaviour is to ensure we don't
 *                  lock up workspace playback when a given asset takes longer then expected to load.  Emitted
 *                  on the SLIDE.
 */

const MEDIA_MORPH_LINKED_SPECS: MorphLinkedPropertySpecs<TimeAwareMediaAsset> = {
    flipHorizontally: {},
    flipVertically: {},
    brightness: {},
    contrast: {},
    hue: {},
    saturation: {},
    lightness: {},
    blur: {},
    noise: {},
    noiseSize: {},
    vignetting: {},
    vignettingAlpha: {},
    vignettingBlur: {},
    enableGreyscale: {},
    gradingOverride: {
        isEqual: (a, b) => {
            if (!a.gradingOverride && !b.gradingOverride) return true;
            if (!a.gradingOverride || !b.gradingOverride) return false;
            return isEqual(a.gradingOverride.save(), b.gradingOverride.save());
        },
        sync: (target, source) => {
            if (!source.gradingOverride) {
                target.removeGradingOverride();
            } else {
                target._gradingOverrideCustomLoad(
                    source.gradingOverride.save() as unknown as typeof target.gradingOverride,
                );
            }
        },
    },
};

export abstract class MediaAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = 'MediaAsset';
    static ADJUSTMENT_FILTER_NAME = 'AdjustmentFilter_1';
    static BLUR_FILTER_NAME = 'BlurFilter_1';
    static GREYSCALE_FILTER_NAME = 'GreyscaleFilter_1';
    static HSL_ADJUSTMENT_FILTER_NAME = 'HslAdjustmentFilter_1';
    static OLD_FILM_FILTER_NAME = 'OldFilmFilter_1';
    static COLOR_GRADING_FILTER_NAME = 'ColorGradingFilter_1';
    static XY_TRANSLATION_MARGIN_PX: number = 10;

    #kenburn = Config.NONE_BACKGROUND_ANIMATION;
    #maxWidth: number = 0;
    #maxHeight: number = 0;
    #scaleMultiplier = 1;

    #sourceData: Omit<MediaSourceData, 'duration'> | null = null;
    #playbackRegions: Readonly<Readonly<PlaybackRegion>[]> = [];
    #audioMode: string;

    #mediaTargetResolution = { width: 512, height: 512 };
    // TransformMatrix extends mixins that don't expose constructor params properly in TS
    #transformMatrix: TransformMatrix = new (TransformMatrix as unknown as new (settings: {
        parent: MediaAsset;
    }) => TransformMatrix)({
        parent: this,
    });

    #gradingOverride: ColorGradingSettings | null = null;

    #sprite: PIXI.Sprite = new PIXI.Sprite();
    #resource: PIXI.ImageResource | PIXI.VideoResource | WebpResource | PIXI.SVGResource | null =
        null;
    /**
     * store a reference of the original #resource.source so on cleanup we can correctly remove event listeners
     * because on playback sometimes we switch the underlying <video/> to a different playback region
     */
    #originalResourceVideo: HTMLVideoElement | null = null;
    /**
     * Array of video elements, each representing a playback region. Each are pre-seeked to optimize continuous playback.
     */
    #playbackRegionSources: HTMLVideoElement[] = [];
    /**
     * Array of scheduled playback callbacks, used to remove them when playback regions change.
     */
    #playbackRegionCallbacks: Array<(assetTime: number) => void> = [];

    #colorGradingManager: ColorGradingManager = new ColorGradingManager({
        parent: this,
    });

    #audioAsset: AudioAsset;

    #failed = false;

    #onBufferingTimeoutFallback: ReturnType<typeof setTimeout> | null = null;
    #abortBuffering: (error: BufferingAbortedError) => void = () => {};

    #fillMode: FillMode = Config.FILL_COVER;

    #seekWaitTime: number;

    #sourceUrl: string | null = null;
    #loadingSourceUrl: string | null = null;
    #sourceMimeType: 'video/mp4' | null = null;
    #sourceDuration: number | null = null;
    #initialSourceLoadComplete = false;

    @BooleanAttribute()
    accessor linkToSceneGroup = false;
    @BooleanAttribute()
    accessor flipHorizontally = false;
    @BooleanAttribute()
    accessor flipVertically = false;

    #linkedMediaAsset: TimeAwareMediaAsset | CompositingAsset | null = null;

    /**
     * The container used by kenburns to scale the media. These scale values may be animated over time.
     */
    protected _kenburnsScaleContainer = new PIXI.Container();
    /**
     * The container used for static scaling and rotating (do not change as time progresses),
     * these are the values that the user will specify.
     */
    #scaleRotateContainer = new PIXI.Container();
    /**
     * The container used for dynamic animations (these are set up in the scene design and
     * will change as time progresses).
     */
    #dynamicAnimationContainer = new PIXI.Container();

    #mediaSizeToUse: MediaSize;

    /**
     * @param container - the container we want to house this asset under
     * @param settings - configuration object
     */
    // TODO(strict-ts): Change container type when BaseAsset.ts is updated
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: MediaAssetSettings) {
        super(container, settings);

        this.#audioMode = settings.audioMode;
        this.#seekWaitTime = settings.seekWaitTime;
        this.#mediaSizeToUse = settings.mediaSizeToUse;

        this._kenburnsScaleContainer.name = 'kenburnsScaleContainer';
        this.#scaleRotateContainer.name = 'scaleRotateContainer';
        this.#dynamicAnimationContainer.name = 'dynamicAnimationContainer';

        /**
         * The order of the containers matters. Transformations are applied bottom up, so we want to organize the
         * transformations so that translating happens last. We want scale & rotations to happen first, so that we maintain
         * the same origin for scale & rotate regardless of the translation. This is how the current hierarchy is set up:
         * ->    MediaAsset (this)
         *        - this is where translations are applied. All static translations are set up on this Container, but
         *          dynamic animations still happen on the lower container (which we may want to change eventually)
         *        - kenburns applies its animated translations here too, but the animation accounts for the static
         *          translations applied before it
         *        - masks are applied at this level
         * -->   kenburnsScaleContainer
         *        - this container should only be used for kenburns scaling
         *        - we want this above the scaleRotateContainer so that the kenburns always happen in the x & y direction
         *          of the scene. If the asset is rotated by the user we don't want the kenburns to rotate with it.
         *        - since we move the pivot, we need to be careful not to set x/y values directly (offsetting is okay)
         * --->  scaleRotateContainer
         *        - this container is used for static flipping & rotating (done by the user)
         *        - since we move the pivot, we need to be careful not to set x/y values directly (offsetting is okay)
         *        - dynamic graphics are also attached to this container, so that they flip & rotate with the media
         * ----> dynamicAnimationContainer
         *        - this container is used by dynamic styling animations
         *        - we use a separate container so it doesn't conflict with user edits or kenburns (lets them stack)
         *        - since we move the pivot, we need to be careful not to set x/y values directly (offsetting is okay)
         *        - technically if we have position animations that do not offset (i.e. are absolute), they won't work
         *          as expected (they should be done at the top level with the other translations), but we don't have
         *          that use case right now for media asset so this is fine
         *        - the media sprite is attached to this container
         *        - filters are applied at this level
         */
        this._addPixiChild(this._kenburnsScaleContainer);
        this._kenburnsScaleContainer.addChild(this.#scaleRotateContainer);
        this.#scaleRotateContainer.addChild(this.#dynamicAnimationContainer);

        // resize image properties
        this.fillMode = settings.fillMode || Config.FILL_COVER;
        this.#mediaTargetResolution = this.getReferenceResolution();
        this.#maxWidth = this.#mediaTargetResolution.width;
        this.#maxHeight = this.#mediaTargetResolution.height;
        this.#dynamicAnimationContainer.addChild(this.#sprite);

        // events
        this.on('onLoaded', this.onLoaded, this);

        // audio
        this.#audioAsset = new AudioAsset({
            fetchResources: settings.fetchResources,
            name: `audio_${this.name}`,
            parent: this,
            autoUpdate: this.autoDraw,
            getAudioContext: () => this.root.audioContext,
            getIsExternallyMuted: () => this.root.muted,
        });

        this.#colorGradingManager.on('onLoaded', this.#emitSourceLoaded, this);
    }

    /**
     * We want to separate the filters from the masks, so that masks will also clip filters.
     * This means we apply the filter on a lower container while the mask is applied on a higher container.
     */
    override get _pixiFilterContainer(): PIXI.DisplayObject {
        // We use the bottom container not only so that masks clip filters, but also so that
        // filters don't apply to any dynamic graphics.
        return this.#dynamicAnimationContainer;
    }

    /**
     * Emits the onLoaded event indicating the asset has loaded.
     */
    #emitSourceLoaded = () => {
        if (
            this.textureSourceDomElement instanceof HTMLVideoElement &&
            !isVideoElementLoaded(this.textureSourceDomElement)
        ) {
            return;
        }
        if (!this.#playbackRegionSources.every(isVideoElementLoaded)) {
            return;
        }
        if (!this.#colorGradingManager.isLoaded) {
            return;
        }
        this.emit('onLoaded');
        if (this.canPlay) {
            this._emitAssetCanPlay();
        }
    };

    /**
     * Used to internally communicate that a load attempt failed.
     */
    #loadAttemptFailed() {
        this.#failed = true;
        this.emit('onLoadAttemptFailed');
    }

    /**
     * Emits the onBufferingDone event indicating that the asset finished loading
     * the content for the requested time.
     * @internal
     */
    _onSourceBufferingDone = () => {
        this.emit('onBufferingDone');
    };

    /**
     * @internal
     */
    get _resource():
        | PIXI.ImageResource
        | PIXI.VideoResource
        | WebpResource
        | PIXI.SVGResource
        | null {
        return this.#resource;
    }

    /**
     * The sprite which contains the texture we render
     * @internal
     */
    get _sprite() {
        return this.#sprite;
    }

    get mediaTargetResolution() {
        return this.#mediaTargetResolution;
    }

    set fillMode(value) {
        if (value === this.fillMode) {
            return;
        }
        assertInArray(value, Config.ALL_FILL_MODES);
        this.#fillMode = value;
        this.requiresRedraw = true;
    }

    get fillMode() {
        return this.#fillMode;
    }

    /**
     * @deprecated use sourceData.uuid instead
     */
    get mediaId(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaId;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.uuid ?? null;
    }

    /**
     * @deprecated use sourceData.title instead
     */
    get title(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.title;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.title ?? null;
    }

    get startTime(): number {
        return PlaybackRegions.getStartTime(this.#playbackRegions);
    }

    /**
     * The time in milliseconds into the media when playback should start.
     */
    set startTime(value: number) {
        if (this.linkToSceneGroup) {
            throw new Error('Cannot control time for a media that is linked to the scene group.');
        }
        if (!this.isVideo && value > 0) {
            // we're setting startTime to a specific value for an image asset which isn't supported
            throw new Error('invalid: cannot set startTime for an image source');
        }
        const sourceDuration = this.sourceDuration;
        if (sourceDuration === null) {
            throw new Error('Cannot set startTime when sourceDuration is null');
        }
        if (value === undefined || value === null) {
            value = 0; // set back to default value
        }
        const playbackRegionStartTime = PlaybackRegions.getStartTime(this.#playbackRegions);
        if (value === playbackRegionStartTime) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);

        this.#playbackRegions = PlaybackRegions.setStartTime(
            value,
            this.#playbackRegions,
            sourceDuration,
        );

        this.requiresRedraw = true;
    }

    /**
     * The time in milliseconds into the media when playback should end.
     */
    set endTime(value: number | null) {
        if (this.linkToSceneGroup) {
            throw new Error('Cannot control time for a media that is linked to the scene group.');
        }
        if (!this.isVideo && value !== null && value >= 0) {
            // we're setting endTime to a specific value for an image asset which isn't supported
            throw new Error(`invalid: cannot set endTime for an image source`);
        }
        if (this.sourceDuration === null) {
            throw new Error('Cannot set startTime when sourceDuration is null');
        }
        if (value === undefined || value === null || value < 0) {
            // playbackRegions cannot be nullable, so default value is the sourceDuration
            value = this.sourceDuration;
        }
        // cap the endTime to the duration
        if (value > this.sourceDuration) {
            value = this.sourceDuration;
        }
        const playbackRegionEndTime = PlaybackRegions.getEndTime(this.#playbackRegions);
        if (value === playbackRegionEndTime) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);

        this.#playbackRegions = PlaybackRegions.setEndTime(
            value,
            this.#playbackRegions,
            this.sourceDuration,
        );
        this.requiresRedraw = true;
    }

    get endTime(): number | null {
        if (this.#sourceData === null || !this.isVideo) {
            return null;
        }
        const playbackRegionEndTime = PlaybackRegions.getEndTime(this.#playbackRegions);
        return playbackRegionEndTime;
    }

    /**
     * Duration after cuts are applied, in milliseconds
     */
    get duration(): number {
        return PlaybackRegions.getDurationFromPlaybackRegions(this.#playbackRegions);
    }

    /**
     * @deprecated use sourceData.source_id instead
     */
    get mediaSourceId(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaSourceId;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.source_id;
    }

    /**
     * @deprecated use sourceData.license instead
     */
    get license(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.license;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.license;
    }

    /**
     * @deprecated use sourceData.is_editorial instead
     */
    get isEditorial(): boolean | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.isEditorial;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.is_editorial;
    }

    /**
     * @deprecated use sourceData.source instead
     */
    get mediaSource(): number | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaSource;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.source;
    }

    /**
     * @deprecated use sourceData.type instead
     */
    get mediaAssetType(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaAssetType;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.type;
    }

    /**
     * @deprecated use sourceData.width instead
     */
    get sourceRenderWidth(): number | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceRenderWidth;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.width;
    }

    /**
     * @deprecated use sourceData.height instead
     */
    get sourceRenderHeight(): number | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceRenderHeight;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.height;
    }

    /**
     * The kenburn or ken burns effect is an animation preset which gets applied on the overall media element. The kenburn
     * string value gets converted into a number of animation curves which drive the necessary properties during
     * `applyAnimation` to achieve the necessary effect such as `zoom_in` or `pan_left`.
     */
    get kenburn(): string {
        return this.#kenburn;
    }

    set kenburn(value) {
        if (this.kenburn === value) {
            return;
        }
        assertInEnum(value, Config.ALL_BACKGROUND_ANIMATIONS);
        this.#kenburn = value;
        this.requiresRedraw = true;
    }

    /**
     * The maximum width of the media asset.
     */
    get maxWidth(): number {
        return this.#maxWidth;
    }

    set maxWidth(value: number) {
        if (this.maxWidth === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#maxWidth = value;
        this.requiresRedraw = true;
    }

    /**
     * The maximum height of the media asset.
     */
    get maxHeight(): number {
        return this.#maxHeight;
    }

    set maxHeight(value: number) {
        if (this.maxHeight === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#maxHeight = value;
        this.requiresRedraw = true;
    }

    /**
     * Returns the approximate biggest possible bounds. For media assets with fillmode = cover this should be pretty
     * accurate to the bounds it'll take up. For media assets with other fillmodes their size depends on their
     * source dimensions and this method will return the maximum dimensions.
     *
     * If there is a mask, then the bounds of the mask is returned.
     */
    override get approximateMaxBounds(): PIXI.Rectangle {
        if (this.mask instanceof StencilMask) {
            return this.mask.getMaxBounds();
        }
        const { width, height } = (() => {
            let width = this.maxWidth;
            let height = this.maxHeight;

            // if we have source data set then account for the fill mode
            if (this.#sourceData && this.#sourceData.height && this.#sourceData.width) {
                const fillModeResult = calculateScaledResolution(
                    { width: this.#sourceData.width, height: this.#sourceData.height },
                    { width, height },
                    this.fillMode,
                );
                width = fillModeResult.width;
                height = fillModeResult.height;
            }
            return { width, height };
        })();
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = getPositionFromTransformation(transformation, width, height);

        return new PIXI.Rectangle(x, y, width, height);
    }

    /**
     * Some layouts, such as ImageMoveLarge animate in from outside the final mask area and
     * therefore need extra 'overscan' image information. A scale multiplier can be set
     * specifically for the few layouts requiring this special treatment. If the assigned scale
     * multiplier value seems a bit high, it's because of the sizing required for zoom-style
     * kenburns animation.
     *
     * WARNING: Avoid using this whenever possible as it results in the final cropping looking
     * different than what was defined by the user.
     */
    get scaleMultiplier(): number {
        return this.#scaleMultiplier;
    }

    set scaleMultiplier(value: number) {
        if (this.scaleMultiplier === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#scaleMultiplier = value;
        this.requiresRedraw = true;
    }

    _gradingOverrideCustomLoad(grading: ColorGradingSettings | null) {
        if (!grading) {
            return;
        }
        this.addGradingOverride();
        if (!this.#gradingOverride) {
            throw new Error('Failed to add grading override');
        }
        this.#gradingOverride.load(grading as unknown as Record<string, unknown>);
    }

    addGradingOverride() {
        if (this.#gradingOverride) {
            return;
        }
        this.#gradingOverride = new ColorGradingSettings({
            parent: this.#colorGradingManager,
            fetchResources: this.fetchResources,
        });
        this.#colorGradingManager.updateFilterProperties();
        this.requiresRedraw = true;
    }

    removeGradingOverride() {
        if (!this.#gradingOverride) {
            return;
        }
        this.#gradingOverride.destroy();
        this.#gradingOverride = null;
        this.#colorGradingManager.updateFilterProperties();
        this.requiresRedraw = true;
    }

    get gradingOverride(): ColorGradingSettings | null {
        return this.#gradingOverride;
    }

    /**
     * Returns the linked media asset. This asset will only be present when linkToSceneGroup is true and the linked
     * asset is loaded.
     */
    get linkedAsset(): TimeAwareMediaAsset | CompositingAsset | null {
        return this.#linkedMediaAsset;
    }

    get isLinkedToCompositingAsset(): boolean {
        if (!this.#linkedMediaAsset) {
            return false;
        }
        return Config.isCompositingAsset(this.#linkedMediaAsset as CompositingAsset);
    }

    get isLinkedToMediaAsset(): boolean {
        if (!this.#linkedMediaAsset) {
            return false;
        }
        return Config.isMediaAsset(this.#linkedMediaAsset as TimeAwareMediaAsset);
    }

    /**
     * @internal
     */
    linkSourceTo(asset: TimeAwareMediaAsset | CompositingAsset) {
        if (this.#linkedMediaAsset === asset) {
            return;
        }

        this.#clearExceptTransformations();
        this.#linkedMediaAsset = asset;
        if (
            asset.isSourceDataSet &&
            asset.isLoaded &&
            !(
                typeof asset.fetchResources !== 'boolean' &&
                asset.fetchResources.media === FETCH_ON_BUFFER
            )
        ) {
            this.requiresRedraw = true;
        } else {
            asset.once('onLoaded', () => {
                if (this.destroyed) {
                    return;
                }
                if (!this.#linkedMediaAsset || this.#linkedMediaAsset !== asset) {
                    // If we get here, the asset was cleared
                    return;
                }
                this.requiresRedraw = true;
            });
        }
    }

    get activeResource():
        | PIXI.ImageResource
        | PIXI.VideoResource
        | PIXI.CanvasResource
        | PIXI.SVGResource
        | WebpResource
        | null {
        return this.#linkedMediaAsset?.activeResource || this.#resource;
    }

    get originalResourceResolution(): { width: number; height: number } {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.originalResourceResolution;
        }
        if (this.#resource) {
            return {
                width: this.#resource.width,
                height: this.#resource.height,
            };
        }
        return {
            width: 0,
            height: 0,
        };
    }

    #getResolutionToFitContainer(
        container: { width: number; height: number },
        object: { width: number; height: number },
    ) {
        const aspectRatio = object.width / object.height;

        // First try fitting by width
        const width = container.width;
        const height = width / aspectRatio;

        // If height exceeds container, fit by height instead
        if (height > container.height) {
            return { height: container.height, width: container.height * aspectRatio };
        }
        return { width, height };
    }

    _animateOldFilmFilter() {
        // If the old film filter is used, update the seed to animate the noise
        const oldFilmFilter = this.#getOldFilmFilter();
        if (oldFilmFilter) {
            oldFilmFilter.seed = Math.random();
        }
    }

    override updateForRender() {
        super.updateForRender();
        this._animateOldFilmFilter();
        if (!this.isSourceValid) {
            return;
        }
        if (this.#linkedMediaAsset) {
            this.#linkedMediaAsset.updateForRender();
        }
        const resource = this.activeResource;
        if (!resource || resource.destroyed) {
            return;
        }
        if (!resource.valid) {
            return;
        }

        this.#sprite.texture = this._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const sprite = this.#sprite;
        const sharedTexture = this.#sprite.texture;

        const { spriteWidth, spriteHeight } = this.#getSpriteSize();
        this.#sprite._width = spriteWidth;
        this.#sprite._height = spriteHeight;

        sprite.scale._x = 1;
        sprite.scale._y = 1;

        const originalMediaResolution = this.originalResourceResolution;
        const scaledMediaTextureResolution = this.#getResolutionToFitContainer(
            { width: sharedTexture.width, height: sharedTexture.height },
            calculateScaledResolution(
                originalMediaResolution,
                { width: sharedTexture.width, height: sharedTexture.height },
                this.fillMode,
            ),
        );
        sharedTexture.trim = new PIXI.Rectangle(
            0,
            0,
            scaledMediaTextureResolution.width,
            scaledMediaTextureResolution.height,
        );
        sharedTexture.orig = new PIXI.Rectangle(
            0,
            0,
            scaledMediaTextureResolution.width,
            scaledMediaTextureResolution.height,
        );
        sharedTexture.frame = new PIXI.Rectangle(
            0,
            0,
            scaledMediaTextureResolution.width,
            scaledMediaTextureResolution.height,
        );

        const canvas = (
            this.#sprite.texture.baseTexture.resource as unknown as { source: HTMLCanvasElement }
        ).source;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error(
                'Failed to get 2D context from canvas; the canvas has already been set to a different context mode',
            );
        }

        const scaledMediaResolution = this.#getResolutionToFitContainer(
            { width: canvas.width, height: canvas.height },
            calculateScaledResolution(
                originalMediaResolution,
                { width: canvas.width, height: canvas.height },
                this.fillMode,
            ),
        );

        sprite.scale._x = spriteWidth / scaledMediaTextureResolution.width;
        sprite.scale._y = spriteHeight / scaledMediaTextureResolution.height;

        ctx.resetTransform();
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const mediaScale = scaledMediaResolution.width / originalMediaResolution.width;
        ctx.scale(mediaScale, mediaScale);
        ctx.drawImage(resource.source as HTMLVideoElement | HTMLImageElement | VideoFrame, 0, 0);

        this.#sprite.texture.update();
    }

    get activeRenderingCanvas(): HTMLCanvasElement {
        return (
            this.#sprite.texture.baseTexture.resource as unknown as { source: HTMLCanvasElement }
        ).source;
    }

    /**
     * Used to compare the current source data to the new source data. This explicitly only
     * checks properties that are relevant to MediaAsset. Since this data often comes from the
     * database we don't want to consider properties that are otherwise ignored.
     */
    #isSourceDataEqual(data: MediaSourceData): boolean {
        if (!this.#sourceData) {
            return false;
        }
        if (isEmpty(data)) {
            return isEmpty(this.#sourceData);
        }
        const checkOptional = <T>(a: T | null | undefined, b: T | null | undefined) => {
            if (!a) {
                return !!b;
            }
            return a !== b;
        };
        if (data.uuid !== this.#sourceData.uuid) {
            return false;
        }
        if (checkOptional(data.title, this.#sourceData.title)) {
            return false;
        }
        if (data.type !== this.#sourceData.type) {
            return false;
        }
        if (data.source !== this.#sourceData.source) {
            return false;
        }
        if (data.source_id !== this.#sourceData.source_id) {
            return false;
        }
        if (data.license !== this.#sourceData.license) {
            return false;
        }
        if (data.is_editorial !== this.#sourceData.is_editorial) {
            return false;
        }
        if (data.width !== this.#sourceData.width) {
            return false;
        }
        if (data.height !== this.#sourceData.height) {
            return false;
        }
        if (data.thumbnail_url !== this.#sourceData.thumbnail_url) {
            return false;
        }
        if (data.preview_url !== this.#sourceData.preview_url) {
            return false;
        }
        if (data.render_url !== this.#sourceData.render_url) {
            return false;
        }
        if (this.isVideo) {
            if (checkOptional(data.poster_url, this.#sourceData.poster_url)) {
                return false;
            }
            if (checkOptional(data.has_audio, this.#sourceData.has_audio)) {
                return false;
            }
            if (checkOptional(data.has_voiceover, this.#sourceData.has_voiceover)) {
                return false;
            }
            // Skip checking duration because MediaAsset updates it after loading
        }
        // Any other key difference we don't care about, so return true
        return true;
    }

    #updateSourceData(sourceData: MediaSourceData) {
        const passedInDuration = sourceData.duration;
        // the source of truth for duration will be this.#sourceDuration.
        // Avoid storing it in a second place (within sourceData) to avoid confusion.
        const { duration: _, ...sourceDataCopy } = sourceData;
        this.#sourceData = sourceDataCopy;

        const newSourceUrl = (() => {
            const mediaSizeToUse = this.#mediaSizeToUse;
            if (mediaSizeToUse === Config.THUMBNAIL_MEDIA_SIZE && this.thumbnailUrl) {
                return this.thumbnailUrl;
            }
            if (mediaSizeToUse === Config.PREVIEW_MEDIA_SIZE && this.previewUrl) {
                return this.previewUrl;
            }
            if (mediaSizeToUse === Config.RENDER_MEDIA_SIZE && this.renderUrl) {
                return this.renderUrl;
            }
            ErrorReporter.setContext('MediaAsset', {
                sourceData,
                mediaSize: mediaSizeToUse,
            });
            throw new Error('No valid media sources to load');
        })();

        this.#sourceUrl = maybeProxy(newSourceUrl);
        this.#sourceMimeType =
            sourceData.type === Config.VIDEO_TYPE_BACKGROUND ? 'video/mp4' : null;
        this.#sourceDuration = passedInDuration ?? null;

        if (
            this.#audioMode === Config.AUDIO_MODES.RENDER &&
            this.#sourceData.has_audio &&
            !this.#linkedMediaAsset
        ) {
            // loading is done through the audioAsset in this case
            this.#audioAsset.url = this.#sourceData.audio_url || this.#sourceUrl;
        }
        this.#initialSourceLoadComplete = false;

        this.requiresRedraw = true;
    }

    /**
     * Set source data. Derived from our media asset database, the source data contains a list of media size urls plus
     * a variety of related properties. All of these properties are fixed as they are owned by the media asset database
     * which is why we don't provide any way of setting or changing the properties.
     */
    set sourceData(data: MediaSourceData) {
        if (isEmpty(data) || this.#isSourceDataEqual(data)) {
            return;
        }
        // validate input
        assertIsValidMediaSourceData(data);

        if (this.#loadingSourceUrl !== null) {
            this.clear();
        }
        if (this.isSourceDataSet) {
            // Only want to reset the transformations if we're changing the source data. For example, during
            // load, we don't want to reset the transformations otherwise we'll overwrite values being loaded.
            this.resetEditableTransformations();
        }

        // for things like scene swapper, when sourceData is copied, start/end times are custom loaded
        // we require playbackRegions to be set in order for start/end times to be properly loaded
        if (
            data.type === Config.VIDEO_TYPE_BACKGROUND &&
            'duration' in data &&
            typeof data.duration === 'number'
        ) {
            this.#playbackRegions = [[0, data.duration]];
        } else {
            // when an asset switches from video to image, we should clear playback regions
            this.#playbackRegions = [];
        }

        this.#updateSourceData(data);
    }

    /**
     * Used to change the source data of the media asset without resetting properties like volumne, trim, and transformations.
     * The new source must have the same visual piece of media
     *
     * Use cases: audio enhancement, transcoding
     */
    setSourceDataWithoutResettingProperties(data: MediaSourceData) {
        if (this.#isSourceDataEqual(data)) {
            return;
        }
        // validate input
        assertIsValidMediaSourceData(data);

        this.#updateSourceData(data);
    }

    get isSourceDataSet(): boolean {
        return this.#sourceData !== null;
    }

    override isMorphMatch(other: BaseAsset): boolean {
        const otherMedia = other as MediaAsset;
        if (!Config.isMediaAsset(otherMedia as TimeAwareMediaAsset)) {
            return false;
        }

        if (!this.sourceData.uuid || !otherMedia.sourceData.uuid) {
            return false;
        }
        if (this.sourceData.uuid !== otherMedia.sourceData.uuid) {
            return false;
        }

        const matchingStartTime = this.startTime === otherMedia.startTime;
        if (!matchingStartTime) {
            return false;
        }

        if (
            this.mask &&
            otherMedia.mask &&
            (this.mask as StencilMask)._classType !== (otherMedia.mask as StencilMask)._classType
        ) {
            return false;
        }

        if (
            !MorphManager.areLinkedPropertiesEqual(
                this as unknown as TimeAwareMediaAsset,
                otherMedia as TimeAwareMediaAsset,
                MEDIA_MORPH_LINKED_SPECS,
            )
        ) {
            return false;
        }

        return true;
    }

    override syncMorphLinkedProperties(other: BaseAsset): boolean {
        const otherMedia = other as TimeAwareMediaAsset;
        if (!Config.isMediaAsset(otherMedia)) return false;

        return MorphManager.syncLinkedProperties(
            this as unknown as TimeAwareMediaAsset,
            otherMedia,
            MEDIA_MORPH_LINKED_SPECS,
        );
    }

    /**
     * Get source data for a media asset.
     */
    get sourceData(): MediaSourceData {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceData ?? ({} as MediaSourceData);
        }
        if (this.#sourceData === null) {
            return {} as MediaSourceData;
        }
        const data: MediaSourceData = {
            uuid: this.#sourceData.uuid,
            title: this.#sourceData.title,
            type: this.#sourceData.type,
            source: this.#sourceData.source,
            source_id: this.#sourceData.source_id,
            license: this.#sourceData.license,
            is_editorial: this.#sourceData.is_editorial,
            width: this.#sourceData.width,
            height: this.#sourceData.height,
            thumbnail_url: this.#sourceData.thumbnail_url,
            preview_url: this.#sourceData.preview_url,
            render_url: this.#sourceData.render_url,
        };
        if (this.isVideo) {
            data.poster_url = this.#sourceData.poster_url;
            data.duration = this.#sourceDuration ?? undefined;
            data.has_audio = this.#sourceData.has_audio;
            data.has_voiceover = this.#sourceData.has_voiceover;
            data.ai_avatar = this.#sourceData.ai_avatar;
            data.audio_url = this.#sourceData.audio_url;
        }
        return data;
    }

    /**
     * @deprecated use sourceData.thumbnail_url instead
     */
    get thumbnailUrl(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.thumbnailUrl;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.thumbnail_url;
    }

    /**
     * @deprecated use sourceData.preview_url instead
     */
    get previewUrl(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.previewUrl;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.preview_url;
    }

    /**
     * @deprecated use sourceData.render_url instead
     */
    get renderUrl(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.renderUrl;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.render_url;
    }

    /**
     * @deprecated use sourceData.poster_url instead
     */
    get posterUrl(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.posterUrl;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.poster_url ?? null;
    }

    /**
     * The duration of the source in milliseconds.
     */
    get sourceDuration(): number | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceDuration;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceDuration;
    }

    protected _loadSourceWithNetworkManager(ignoreShouldLoad = false) {
        if (!this._shouldLoad && !ignoreShouldLoad) {
            return;
        }
        const shouldLoadSource = this.#loadingSourceUrl !== this.#sourceUrl;
        if (!shouldLoadSource) {
            return;
        }
        this.#loadingSourceUrl = this.#sourceUrl;
        this._assetNetworkManager.waitForNetworkSlot(this, (requestFinished) => {
            this.#loadSource(requestFinished);
        });
    }

    /**
     * CREATE VIDEO
     * NOTE: DO NOT mute before load, as Chrome bugs and creates a WebGL error
     *       https://bugs.chromium.org/p/chromium/issues/detail?id=898550
     *
     * Preloading:
     * The only reason we create the video element ourselves is to avoid PIXI setting preload="auto".
     * Preloading causes seeking to be super slow for some videos.
     * It requires more network and processing, especially with trimmed media, where
     * it seems that browsers try to load all the file instead of seeking.
     */
    #createVideoResource() {
        if (!this.#sourceUrl) {
            throw new Error('Cannot create video resource without a source URL.');
        }
        if (!this.#sourceMimeType) {
            throw new Error('Cannot create video resource without a source MIME type.');
        }
        const videoElement = document.createElement('video');
        videoElement.setAttribute('preload', 'none');
        videoElement.setAttribute('webkit-playsinline', '');
        videoElement.setAttribute('playsinline', '');

        PIXI.BaseImageResource.crossOrigin(videoElement, this.#sourceUrl);
        const sourceElement = document.createElement('source');
        sourceElement.src = this.#sourceUrl;
        sourceElement.type = this.#sourceMimeType;
        videoElement.appendChild(sourceElement);
        const resource = new PIXI.VideoResource(videoElement, {
            autoLoad: false,
            autoPlay: false,
        });
        resource.autoUpdate = false;
        videoElement.setAttribute('data-name-for-debugging', 'original');

        return resource;
    }

    /**
     * Create a video element for each playback region. They will be seeked via bufferAtTime when needed.
     */
    #createVideoSourcesForPlaybackRegions() {
        if (
            !this.#resource ||
            !this.#resource.source ||
            !PIXI.VideoResource.test(this.#resource.source)
        ) {
            throw new Error('Cannot create playback region video without a valid video resource.');
        }
        const sourceVideoElement = this.#resource.source.cloneNode(true) as HTMLVideoElement;
        sourceVideoElement.load();
        sourceVideoElement.setAttribute(
            'data-name-for-debugging',
            `playbackRegion-${this.#playbackRegionSources.length}`,
        );
        sourceVideoElement.addEventListener('canplay', this.#emitSourceLoaded);
        sourceVideoElement.addEventListener('error', this.#loadAttemptFailed);
        return sourceVideoElement;
    }

    #cleanupDetachedVideoElement(videoElement: HTMLVideoElement) {
        if (!videoElement) {
            throw new Error('Playback region video could not be cleaned up.');
        }
        if (videoElement === this.#originalResourceVideo) {
            return;
        }
        videoElement.removeEventListener('canplay', this.#emitSourceLoaded);
        videoElement.removeEventListener('error', this.#loadAttemptFailed);

        // reset internal states and remove references to source
        videoElement.pause();
        videoElement.src = '';
        videoElement.load();
    }

    /**
     * Clears all playback regions on this.clear()
     */
    _clearPlaybackRegionSources() {
        if (this.linkToSceneGroup || this.#linkedMediaAsset) {
            return;
        }

        this.#playbackRegionSources.forEach((obsoleteVideo) => {
            this.#cleanupDetachedVideoElement(obsoleteVideo);
        });
        this.#playbackRegionSources = [];
    }

    /**
     * Destroy current video resources for playback regions and recreates them via cloning the source element.
     */
    _updatePlaybackRegionSources() {
        if (
            !this.isVideo ||
            this.linkToSceneGroup ||
            this.#linkedMediaAsset ||
            !this.#resource ||
            !(this.#resource.source instanceof HTMLVideoElement) ||
            this.#resource instanceof WebpResource
        ) {
            return;
        }

        if (
            typeof this.fetchResources !== 'boolean' &&
            this.fetchResources.media === FETCH_ON_BUFFER
        ) {
            this.#resource.source = this.#originalResourceVideo!;
            return;
        }

        // in here, if we've added a playback region we should add a video source
        const playbackRegionToSourcesDelta =
            this.#playbackRegions.length - this.#playbackRegionSources.length;
        if (this.#originalResourceVideo && playbackRegionToSourcesDelta < 0) {
            // remove videos starting from the end of the array until we have the same amount of videos as playback regions
            for (let i = 0; i < Math.abs(playbackRegionToSourcesDelta); i++) {
                const obsoleteVideo = this.#playbackRegionSources.pop();
                if (obsoleteVideo === this.#resource.source) {
                    /**
                     * if we've switched the underlying <video/> to a playback region's video and we are about to
                     * clean it up, switch the resource back to the original video. We never delete the original
                     * video because our load event handlers are attached to that <video/>.
                     * We will switch the video sources again on seek/playback depending on the time so this switch
                     * won't affect playback.
                     */
                    this.#resource.source = this.#originalResourceVideo;
                }
                if (obsoleteVideo) {
                    this.#cleanupDetachedVideoElement(obsoleteVideo);
                }
            }
        } else if (playbackRegionToSourcesDelta > 0) {
            // we have more playback regions than video sources, create more
            for (let i = 0; i < playbackRegionToSourcesDelta; i++) {
                let videoSource: HTMLVideoElement;
                if (this.#playbackRegionSources.length === 0) {
                    // use the main resource as our first video source
                    videoSource = this.#resource.source;
                    this.#originalResourceVideo = this.#resource.source;
                } else {
                    // subsequent playback region videos are clones of the main resource
                    videoSource = this.#createVideoSourcesForPlaybackRegions();
                    // Reset sentCanPlay so _emitAssetCanPlay can emit again when new sources load
                    this.sentCanPlay = false;
                }

                this.#playbackRegionSources.push(videoSource);
            }
        } else {
            // they're equal, don't need to load anymore sources
        }

        this.#audioAsset._playbackRegionsCustomLoad(this.#playbackRegions as PlaybackRegion[]);

        this.#audioAsset.mediaElements = this.#playbackRegionSources;
    }

    /**
     * @internal
     */
    get _playbackRegionSources() {
        return this.#playbackRegionSources;
    }

    /**
     * @internal
     */
    get _audioAsset() {
        return this.#audioAsset;
    }

    /**
     * Load the `sourceUrl` specified during `sourceData`.
     */
    #loadSource(requestFinished: () => void) {
        if (this.destroyed) {
            requestFinished();
            return;
        }
        if (this.#sourceData === null) {
            requestFinished();
            return;
        }
        if (this.#linkedMediaAsset) {
            void this.#linkedMediaAsset.onSourceLoaded().finally(() => {
                setTimeout(() => {
                    this.requiresRedraw = true;
                }, 0); // allow the event loop to process other events
            });
            requestFinished();
            return;
        }

        const isSvg =
            this.#sourceData.type === Config.IMAGE_TYPE_BACKGROUND &&
            !!this.#sourceUrl &&
            /\.svg(?:[?#]|$)/i.test(this.#sourceUrl);

        this.#resource = (() => {
            if (WebpResource.isWebp(this.#sourceUrl)) {
                if (!this.#sourceUrl) {
                    throw new Error('Cannot create webp resource without a source URL.');
                }
                const webpResource = new WebpResource(this.#sourceUrl, this.#sourceDuration);
                return webpResource;
            }
            if (this.#sourceData.type === Config.VIDEO_TYPE_BACKGROUND) {
                const videoResource = this.#createVideoResource();
                this.#originalResourceVideo = videoResource.source;
                return videoResource;
            }
            if (this.#sourceData.type === Config.IMAGE_TYPE_BACKGROUND) {
                if (!this.#sourceUrl) {
                    throw new Error('Cannot create video resource without a source URL.');
                }
                if (isSvg) {
                    return new PIXI.SVGResource(this.#sourceUrl, {});
                }
                return new PIXI.ImageResource(this.#sourceUrl, { autoLoad: false });
            }
            throw Error('Unknown data type.');
        })();

        this.#resource
            .load()
            .then(() => {
                requestFinished();
                if (!this.destroyed) {
                    this.#emitSourceLoaded();
                    this.requiresRedraw = true;
                }
            })
            .catch((_e: Event) => {
                requestFinished();
                this.#loadAttemptFailed();
            });

        if (this.isLoaded) {
            this.#emitSourceLoaded();
        }
    }

    /**
     * Returns true when the asset is ready for playback. This doesn't necessarily
     * mean that the asset is loaded. It just means that we've given it enough *tries*
     * and thus time to load and we now want to see the result. We do this to ensure
     * we can playback something after and x amount of tries.
     */
    override get canPlay() {
        if (this.destroyed) {
            return false;
        }
        if (!super.canPlay) {
            return false;
        }
        let mediaAssetCanPlay = false;
        if (!this._shouldLoad) {
            mediaAssetCanPlay = true;
        } else {
            mediaAssetCanPlay = this.isLoaded || this.#failed || this.sentCanPlay;
        }
        const audioAssetCanPlay = this.#audioAsset.canPlay;
        return mediaAssetCanPlay && audioAssetCanPlay;
    }

    #onBaseTextureError(_baseTexture: PIXI.BaseTexture, _error: Event) {
        this.#loadAttemptFailed();
    }

    onLoaded() {
        if (this.#sourceData === null || !this.isLoaded) {
            return;
        }
        this.#clearBufferingTimeoutFallback();

        if (!this.isVideo) {
            return;
        }
        if (this.isLinkedToCompositingAsset) {
            this.#sourceDuration = this.#linkedMediaAsset!.sourceDuration;
            this.requiresRedraw = true;
            return;
        }

        // an HTMLMediaElement can update it's duration multiple times, depending on which regions
        // of the video have been buffered. We want to simplify this by allowing it to "correct" the
        // sourceData's initial duration 1x, and then never change the duration again.
        if (this.#initialSourceLoadComplete) {
            return;
        }
        this.#initialSourceLoadComplete = true;

        // if there are multiple HTMLVideoElements (multiple playback regions),
        // we always use the original resource video to get the duration. That's because
        // an HTMLVideoElement can have a duration, depending on what part(s) of the video
        // have been buffered.
        const videoElement = this.#originalResourceVideo;
        if (!videoElement) {
            return;
        }
        // make sure the internal duration matches the loaded <video> duration
        const originalSourceDuration = this.#sourceDuration;
        if (videoElement.duration) {
            const roundedDurationMS = roundToPrecision(
                videoElement.duration * 1000,
                Config.VIDEO_TIME_PRECISION_MS,
            );
            if (this.#sourceDuration !== roundedDurationMS) {
                this.#sourceDuration = roundedDurationMS;
            }
        }
        if (this.#sourceData === null || this.#sourceDuration === null || this.endTime === null) {
            return;
        }
        // ensure the endTime is capped by the sourceDuration
        if (this.endTime > this.#sourceDuration) {
            this.#playbackRegions = PlaybackRegions.cut(
                this.#sourceDuration,
                this.endTime,
                this.endTime,
                this.#playbackRegions,
            );
        } else if (
            originalSourceDuration !== null &&
            this.endTime < this.#sourceDuration &&
            equalWithinEpsilon(originalSourceDuration, this.endTime, 1)
        ) {
            // The sourceDuration can change slightly (e.g. after luminary loads the asset, when source data is replaced via setSourceDataWithoutResettingProperties)
            // If the end time is less than and close to the original source duration, we know the user has not made a cut,
            // so we need to update the PlaybackRegions to include the whole duration
            this.#playbackRegions = PlaybackRegions.setEndTime(
                this.#sourceDuration,
                this.#playbackRegions,
                this.#sourceDuration,
            );
        }
        this.requiresRedraw = true;
    }

    /**
     * For the containers that we use for rotating and scaling, we want that to happen from the center of the media.
     * So this method adjusts the pivot & position of the containers to make sure that happens.
     */
    #centerPivotForScaleContainers() {
        const sprite = this.#sprite;
        if (!sprite) {
            return;
        }
        const width = sprite.width;
        const height = sprite.height;
        const containersToCenter = [
            this._kenburnsScaleContainer,
            this.#scaleRotateContainer,
            this.#dynamicAnimationContainer,
        ];
        containersToCenter.forEach((container) => {
            container.pivot.set(width / 2, height / 2);
            container.position.set(width / 2, height / 2);
        });
    }

    #applyFlip() {
        this.#scaleRotateContainer.scale.x = this.flipHorizontally ? -1 : 1;
        this.#scaleRotateContainer.scale.y = this.flipVertically ? -1 : 1;
    }

    #applyRotation() {
        this.#scaleRotateContainer.rotation = this.transformation.rotation;
    }

    override resetTransformation() {
        super.resetTransformation();
        this.#scaleRotateContainer.rotation = 0;
        this.#scaleRotateContainer.scale.set(1, 1);
    }

    /**
     * Resizes our source to match our media target resolution. Depending on the resizing mode, we either use mipmapping
     * or resize the image ourselves. Both have different visual quality, while mipmapping gives us a softer result,
     * resizing the image is sharper.
     */
    protected resizeMedia() {
        if (!this.isSourceValid) {
            return;
        }

        const { spriteWidth, spriteHeight } = this.#getSpriteSize();

        this.#sprite.width = spriteWidth;
        this.#sprite.height = spriteHeight;
    }

    protected _updateMediaSizeForMorph() {
        this.#mediaTargetResolution = this.#calculateMediaTargetResolution();
        this.resizeMedia();
    }

    #getSpriteSize() {
        let { width: spriteWidth, height: spriteHeight } = calculateScaledResolution(
            this.originalResourceResolution,
            this.#mediaTargetResolution,
            this.fillMode,
        );

        /**
         * Scaling should only work for fill = cover:
         * https://lumen5.atlassian.net/browse/LU-2036
         */
        if (this.fillMode === Config.FILL_COVER) {
            spriteWidth *= this.scaleX;
            spriteHeight *= this.scaleY;
        }

        return { spriteWidth, spriteHeight };
    }

    /**
     * Scales the media to fit into the visible area
     *
     * Undoes the current scaling from the Fill Mode and re-scales the source media to fit into visible area
     */
    setScaleToContainToVisibleArea() {
        // undo current fill mode scale
        const sourceDataResolution = {
            width: this.sourceData.width,
            height: this.sourceData.height,
        };
        const { ratio } = calculateScaledResolution(
            sourceDataResolution,
            this.#mediaTargetResolution,
            this.fillMode,
        );
        let scale = 1 / ratio;

        // set scale for media to be contained in visible area
        const minimumRatio = calculateScaledResolution(
            sourceDataResolution,
            this.#mediaTargetResolution,
            Config.FILL_CONTAIN,
        ).ratio;
        scale *= minimumRatio;

        this.#transformMatrix.scaleX = scale;
        this.#transformMatrix.scaleY = scale;
    }

    /**
     * Clears the bufferingTimeoutFallback timeout. This timeout is setup a part of `bufferAtTime` to ensure our call
     * resolves within a certain amount of time.
     */
    #clearBufferingTimeoutFallback() {
        if (this.#onBufferingTimeoutFallback !== null) {
            clearTimeout(this.#onBufferingTimeoutFallback);
            this.#onBufferingTimeoutFallback = null;
        }
    }

    /**
     * Useful for linking media so we can keep any applied transformations but reset the rest of
     * the media asset state.
     */
    #clearExceptTransformations() {
        // remove buffering and load timeouts
        this.#clearBufferingTimeoutFallback();
        this.#failed = false;
        this.sentCanPlay = false;
        this.#loadingSourceUrl = null;
        const isNotLinkedToOtherAsset = this.#linkedMediaAsset === null;

        // remove resource listeners
        if (this.#sprite) {
            this.#sprite.texture?.baseTexture?.off('loaded', this.#emitSourceLoaded, this);
            this.#sprite.texture?.baseTexture?.off('error', this.#onBaseTextureError, this);
            if (
                this.#sprite.texture?.baseTexture?.hasOwnProperty('source') &&
                this.textureSourceDomElement
            ) {
                this.textureSourceDomElement.removeEventListener(
                    'canplay',
                    this._onSourceBufferingDone,
                );
                this.textureSourceDomElement.removeEventListener(
                    'canplaythrough',
                    this._onSourceBufferingDone,
                );
                this.textureSourceDomElement.removeEventListener(
                    'seeked',
                    this._onSourceBufferingDone,
                );
            }
        }
        if (isNotLinkedToOtherAsset && this.#resource && !this.#resource.destroyed) {
            if (
                this.#resource.source instanceof HTMLVideoElement &&
                this.#originalResourceVideo instanceof HTMLVideoElement &&
                this.#resource.source !== this.#originalResourceVideo &&
                !(this.#resource instanceof WebpResource)
            ) {
                // switch back to the original video source so PIXI can remove event listeners from the correct <video/>
                this.#resource.source = this.#originalResourceVideo;
            }
            this.#resource.destroy();
            this.#originalResourceVideo = null;
        }

        this.#linkedMediaAsset = null;
    }

    /**
     * Clears our object so we can load a source through `#loadSource`.
     */
    override clear() {
        resetTransform(this._pixiContainer);
        this.resetTransformMatrix();
        this.#releaseResources();
    }

    override destroy(options?: DestroyOptions) {
        this.#releaseResources();

        super.destroy(options);

        this.#audioAsset.destroy();
    }

    #releaseResources() {
        this._clearPlaybackRegionSources();
        this.#abortBuffering(
            new BufferingAbortedError(
                this.classType,
                this.luminaryParent?.index ?? -1,
                'resources released',
            ),
        );
        this.#clearExceptTransformations();
    }

    /**
     * Our currently active source url
     */
    get sourceUrl(): string | null {
        return this.#sourceUrl;
    }

    /**
     * Check if `media` is explicitly disabled in fetchResources
     */
    override get _shouldLoad(): boolean {
        if (typeof this.fetchResources === 'boolean') {
            return super._shouldLoad;
        }

        if (this.fetchResources.media === FETCH_ON_BUFFER) {
            if (this.#linkedMediaAsset) {
                return this.#linkedMediaAsset._shouldLoad;
            }
            if (this.#loadingSourceUrl) {
                // We have already started loading the source, which means we did request
                // to load the media already.
                return true;
            }
            return false;
        }
        return !!this.fetchResources && !!this.fetchResources.media;
    }

    override get failedToLoad(): boolean {
        if (super.failedToLoad) {
            return true;
        }
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.failedToLoad;
        }
        return this.#failed;
    }

    get isSourceValid(): boolean {
        if (!this.#sprite) {
            return false;
        }
        if (!this.#sprite.texture) {
            return false;
        }
        if (!this.#sprite.texture.baseTexture) {
            return false;
        }
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.isSourceValid;
        }
        if (!this.activeResource) {
            return false;
        }
        if (!this.activeResource.source) {
            return false;
        }
        return this.activeResource.valid;
    }

    get #isSourceLoaded(): boolean {
        if (!this.#loadingSourceUrl && !this._shouldLoad) {
            return true;
        }
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.isLoaded;
        }
        if (this.#gradingOverride && !this.colorGradingManager.isLoaded) {
            return false;
        }
        if (!this.sourceUrl) {
            return true;
        }
        if (!this.isSourceValid) {
            return false;
        }
        if (!this.luminaryParent || this.luminaryParent?.destroyed) {
            return false;
        }
        if (this.isVideo && !(this.activeResource instanceof WebpResource)) {
            // just checking for duration might be a better way to determine to whether
            // or not the asset is loaded since we've seen some cases in where we got a
            // duration but never valid = true.
            if (!this.textureSourceDomElement) {
                return false;
            }

            if (this.failedToLoad) {
                // This checking definitely make sense for NodeVideoElement.
                // error event can happen during loading assets before calling `bufferAtTime` method
                // TODO: should we return false always when this.failedToLoad for all cases?
                return false;
            }

            if (!isVideoElementLoaded(this.textureSourceDomElement as HTMLVideoElement)) {
                return false;
            }

            if (!this.#playbackRegionSources.every(isVideoElementLoaded)) {
                return false;
            }
        }
        return this.activeResource ? this.activeResource.valid : false;
    }

    /**
     * If there are multiple playback regions, then they all need to be loaded before we consider the asset loaded.
     */
    override get isLoaded(): boolean {
        if (!super.isLoaded) {
            return false;
        }
        return this.#isSourceLoaded;
    }

    get isVideo(): boolean | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.isVideo;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.type === Config.VIDEO_TYPE_BACKGROUND;
    }

    /**
     * Switches the activeResource's video to the correct playback region video, based on assetTime.
     */
    switchPlayback(assetTime: number) {
        // we shouldn't need to switch video sources if there is only 1 playback region
        if (!this.isVideo || this.#playbackRegions.length <= 1) {
            return;
        }

        if (
            !(this.activeResource?.source instanceof HTMLVideoElement) ||
            this.activeResource instanceof WebpResource
        ) {
            return;
        }
        this.activeResource.source.pause();
        const playbackRegionIdx = this.#playbackRegions.findIndex(
            ([start, end]) => assetTime >= start && assetTime < end,
        );
        const videoSource = this.#playbackRegionSources[playbackRegionIdx];
        if (!videoSource) {
            return;
        }
        this.activeResource!.source = videoSource;
        if (this.luminaryParent?.isPlaying) {
            void this.activeResource!.source.play();
            this.#audioAsset.reschedulePlaybackRegionGainCurve(assetTime);
        }
    }

    /**
     * get the dom element for the texture source
     */
    get textureSourceDomElement(): HTMLVideoElement | HTMLImageElement {
        if (this.activeResource instanceof WebpResource) {
            return this.activeResource.source as HTMLVideoElement | HTMLImageElement;
        }
        return (this.activeResource &&
            ((this.activeResource as PIXI.BaseImageResource).source as
                | HTMLVideoElement
                | HTMLImageElement)) as HTMLVideoElement | HTMLImageElement;
    }

    override _propagatePlay() {
        if (this.#linkedMediaAsset) {
            return;
        }
        if (
            this.isVideo &&
            this.isSourceValid &&
            this.textureSourceDomElement &&
            typeof (this.textureSourceDomElement as HTMLVideoElement).play === 'function'
        ) {
            this._schedulePlaybackRegions();
            const playPromise = (this.textureSourceDomElement as HTMLVideoElement).play();

            if (isPromiseLike(playPromise)) {
                playPromise.catch((e: DOMException) => {
                    // ignore AbortError which happens when play interrupted by calling pause.
                    // https://developer.chrome.com/blog/play-request-was-interrupted/
                    if (e.name === 'AbortError') {
                        return;
                    }
                    // we should wrap native error explicitly in order to extend error with callstack
                    throw new Error(e.message);
                });
            } else {
                // for some reason sometimes video element does not return a promise
                // so we need to notify about this case
                ErrorReporter.capture('play method does not return a promise', {
                    level: 'warning',
                });
            }
        }
        this.#audioAsset._propagatePlay();
    }

    override _propagatePause() {
        if (this.#linkedMediaAsset) {
            return;
        }
        if (
            this.isVideo &&
            this.isSourceValid &&
            this.textureSourceDomElement &&
            typeof (this.textureSourceDomElement as HTMLVideoElement).pause === 'function'
        ) {
            (this.textureSourceDomElement as HTMLVideoElement).pause();
        }
        this.#audioAsset._propagatePause();
    }

    /**
     * Calculate the target resolution for our media source.
     */
    #calculateMediaTargetResolution() {
        let width = this.maxWidth;
        let height = this.maxHeight;

        if (this.mask instanceof StencilMask) {
            ({ width, height } = this.mask.getMaxBounds());
        }
        width *= this.scaleMultiplier;
        height *= this.scaleMultiplier;
        return { width, height };
    }

    /**
     * Calculate the position for our MediaAsset so we can place it in the center of the visible area. Here the visible area
     * is determined by the slide and whether our media is affect by a mask.
     */
    #calculateCenterOffset() {
        // position the media asset in the middle of the mask
        const objectWidth = this.#sprite.width;
        const objectHeight = this.#sprite.height;

        if (this.mask instanceof StencilMask) {
            const maxBounds = this.mask.getMaxBounds();
            const midPoint = getMidPointFromRect(maxBounds);
            const x = midPoint.x - objectWidth / 2;
            const y = midPoint.y - objectHeight / 2;
            return { x, y };
        }

        // TODO: replace with maxWidth and maxHeight?
        const referenceResolution = this.getReferenceResolution();
        const x = -(objectWidth - referenceResolution.width) / 2;
        const y = -(objectHeight - referenceResolution.height) / 2;
        return { x, y };
    }

    /**
     * Calculates viewport resolution.
     */
    #getViewportResolution() {
        let viewportResolutionX = 0;
        let viewportResolutionY = 0;
        if (this.mask instanceof StencilMask) {
            const maxBounds = this.mask.getMaxBounds();
            viewportResolutionX = maxBounds.width;
            viewportResolutionY = maxBounds.height;
        } else {
            // TODO: replace with maxWidth and maxHeight?
            const referenceResolution = this.getReferenceResolution();
            viewportResolutionX = referenceResolution.width;
            viewportResolutionY = referenceResolution.height;
        }
        return { viewportResolutionX, viewportResolutionY };
    }

    /**
     * Calculates relative and absolute limits for translation.
     */
    getTranslationLimit(): TranslationLimit {
        const objectWidth = this._pixiContainer.width;
        const objectHeight = this._pixiContainer.height;
        const { viewportResolutionX, viewportResolutionY } = this.#getViewportResolution();
        const maxOffsetX = Math.abs((objectWidth - viewportResolutionX) / 2);
        const maxOffsetY = Math.abs((objectHeight - viewportResolutionY) / 2);
        const tx = parseFloat((maxOffsetX / viewportResolutionX).toFixed(2));
        const ty = parseFloat((maxOffsetY / viewportResolutionY).toFixed(2));
        return {
            x: [-1 * tx, +1 * tx],
            y: [-1 * ty, +1 * ty],
        };
    }

    getScaleLimit(): { x: [number, number]; y: [number, number] } {
        return {
            x: [Config.MEDIA_MIN_SCALE_X, Config.MEDIA_MAX_SCALE_X],
            y: [Config.MEDIA_MIN_SCALE_Y, Config.MEDIA_MAX_SCALE_Y],
        };
    }

    /**
     * Ensure our position or cropping values completely fill the visible area. If that's not the case, then we clamp
     * them here.
     */
    #calculateCropTranslation() {
        const objectWidth = this.#sprite.width;
        const objectHeight = this.#sprite.height;

        const { viewportResolutionX, viewportResolutionY } = this.#getViewportResolution();

        const zoomIsLessThanOne =
            this.#transformMatrix.scaleX < 1.0 || this.#transformMatrix.scaleY < 1.0;

        // TODO: use _transformation object instead? This would remove the need for transformMatrix
        const translationX = (() => {
            const marginX = zoomIsLessThanOne
                ? objectWidth - MediaAsset.XY_TRANSLATION_MARGIN_PX
                : 0.0;
            const maxXOffset = Math.abs((objectWidth - viewportResolutionX) / 2) + marginX;
            const userXOffset = Math.abs(this.#transformMatrix.tx * viewportResolutionX);
            const offsetX = Math.min(maxXOffset, userXOffset);
            const multiX = this.#transformMatrix.tx < 0 ? -1 : 1;
            return offsetX * multiX;
        })();

        const translationY = (() => {
            const marginY = zoomIsLessThanOne
                ? objectHeight - MediaAsset.XY_TRANSLATION_MARGIN_PX
                : 0.0;
            const maxYOffset = Math.abs((objectHeight - viewportResolutionY) / 2) + marginY;
            const userYOffset = Math.abs(this.#transformMatrix.ty * viewportResolutionY);
            const offsetY = Math.min(maxYOffset, userYOffset);
            const multiY = this.#transformMatrix.ty < 0 ? -1 : 1;
            return offsetY * multiY;
        })();

        return {
            x: translationX,
            y: translationY,
        };
    }

    /**
     * Set the position of the MediaAsset
     */
    override applyTransformation(_offsetX = 0, _offsetY = 0) {
        if (!this.#sprite) {
            return;
        }
        if (!this.isSourceValid) {
            return;
        }

        const centerOffset = (() => {
            if (
                // don't automatically center if it is a sticker
                // preference is to decouple this logic from MediaAsset but this is the simplest way to prevent automatic centering
                this.createdFrom !== AssetCreatedFrom.STICKER &&
                this.transformation.position.x === 0 &&
                this.transformation.position.y === 0
            ) {
                return this.#calculateCenterOffset();
            }

            // If a position transformation hasn't been applied, we automatically center the media
            return { x: 0, y: 0 };
        })();

        const cropOffset = this.#calculateCropTranslation();

        // we want to apply rotation around the center of the media asset,
        // so negate it on the base asset and we'll handle it here in Media Asset
        const rotationOffset = -this.transformation.rotation;

        // apply new position
        const offsetX = centerOffset.x - cropOffset.x;
        const offsetY = centerOffset.y - cropOffset.y;
        super.applyTransformation(offsetX, offsetY, rotationOffset);

        this.#centerPivotForScaleContainers();
        this.#applyFlip();
        this.#applyRotation();
    }

    override get autoDraw() {
        return super.autoDraw;
    }

    override set autoDraw(value: boolean) {
        if (this.autoDraw === value) {
            return;
        }
        super.autoDraw = value;

        this.#audioAsset.autoUpdate = value;
    }

    override _setAutoDrawForAllChildren(value: boolean) {
        super._setAutoDrawForAllChildren(value);
        this.#audioAsset.autoUpdate = value;
    }

    /**
     * Applies any necessary animation presets and configurations
     */
    override _applyAnimation() {
        // The old system used animationConfig, which is applied by the container.
        // We don't support both animationConfig and dynamic style, but kenburn animation needs to be applied
        // regardless.
        if (!this.hasDynamicStyle) {
            super._applyAnimation();
            this._applyKenBurn();
        } else if (this.animationConfig.length === 0) {
            this._applyKenBurn();
        }
    }

    _schedulePlaybackRegions() {
        // remove previous callbacks
        this.#playbackRegionCallbacks.forEach((callback) =>
            this.luminaryParent?._timeline.removeCallback(callback),
        );
        this.#playbackRegionCallbacks = [];

        const sourceDataDuration = this.sourceDuration;
        if (
            sourceDataDuration === null ||
            !this.isVideo ||
            !this.isLoaded ||
            this.#playbackRegions.length <= 1 ||
            this.linkToSceneGroup
        ) {
            return;
        }

        // schedule new callbacks
        Array.from(this.#playbackRegions).forEach(([startTime]) => {
            const playbackTime = PlaybackRegions.assetTimeToPlaybackTime(
                this.#playbackRegions,
                sourceDataDuration,
                startTime,
            );
            const switchPlaybackCallback = this.switchPlayback.bind(this);
            this.luminaryParent?._timeline.addCallback(switchPlaybackCallback, playbackTime, [
                startTime,
            ]);
            this.#playbackRegionCallbacks.push(switchPlaybackCallback);
        });
    }

    override _checkAndFixProperties() {
        super._checkAndFixProperties();
        // Ensure that kenburns is set to NONE if this is a video asset
        // because kenburns are only supported for images, not videos!
        if (this.isVideo && this.kenburn !== Config.NONE_BACKGROUND_ANIMATION) {
            this.kenburn = Config.NONE_BACKGROUND_ANIMATION;
        }
        this.#mediaTargetResolution = this.#calculateMediaTargetResolution();

        // Some transitions move the scene, and the mask must move with.
        // Use the internal PIXI container since masks are PIXI objects that need a PIXI parent.
        if (this.mask instanceof PIXI.Container && !this.mask.parent) {
            this.mask.parent = this._requireSlide.scene._pixiContainer;
        }
    }

    /**
     * Reset transform matrix when source data is changed.
     */
    resetTransformMatrix() {
        this.#transformMatrix.reset();
    }

    /**
     * Reset the persisted values of the transformations that users can apply.
     */
    resetEditableTransformations() {
        this.flipHorizontally = false;
        this.flipVertically = false;
        this.transformation.rotation = 0;
        this.scaleX = 1;
        this.scaleY = 1;
        this.xTranslation = 0;
        this.yTranslation = 0;
    }

    _applyKenBurn() {
        // Kenburns is suppressed on both sides of a morph — the zoom/pan would keep running into the
        // morph (cut or slide) and desync it. hasMorphAnimation covers the source scene; isMorphTarget
        // covers the next scene's media (a morph target has no animation of its own).
        if (this.hasMorphAnimation || MorphManager.isMorphTarget(this.id)) {
            this._kenburnsScaleContainer.scale.set(1);
            return;
        }

        const translateX = this._pixiContainer.x;
        const translateY = this._pixiContainer.y;

        const { viewportResolutionX, viewportResolutionY } = this.#getViewportResolution();
        const kenBurnsPanMovement = {
            x: (viewportResolutionX * Config.KENBURN_PAN_PERCENTAGE) / 2,
            y: (viewportResolutionY * Config.KENBURN_PAN_PERCENTAGE) / 2,
        };

        // reset the scale container, so if kenburns is changed, we clear the previous kenburns' scale
        this._kenburnsScaleContainer.scale.set(1);

        switch (this.#kenburn) {
            case Config.PAN_LEFT_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateX - kenBurnsPanMovement.x, translateX + kenBurnsPanMovement.x],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['x'],
                });
                break;
            }
            case Config.PAN_RIGHT_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateX + kenBurnsPanMovement.x, translateX - kenBurnsPanMovement.x],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['x'],
                });
                break;
            }
            case Config.PAN_DOWN_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateY + kenBurnsPanMovement.y, translateY - kenBurnsPanMovement.y],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['y'],
                });
                break;
            }
            case Config.PAN_UP_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateY - kenBurnsPanMovement.y, translateY + kenBurnsPanMovement.y],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['y'],
                });
                break;
            }
            case Config.ZOOM_IN_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_ZOOM_PERCENTAGE;
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [1, scaleFactor],
                        [1, scaleFactor],
                    ],
                    drivenObjects: [this],
                    drivenProperty: [
                        '_kenburnsScaleContainer.scale.x',
                        '_kenburnsScaleContainer.scale.y',
                    ],
                });
                break;
            }
            case Config.ZOOM_OUT_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_ZOOM_PERCENTAGE;
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [scaleFactor, 1],
                        [scaleFactor, 1],
                    ],
                    drivenObjects: [this],
                    drivenProperty: [
                        '_kenburnsScaleContainer.scale.x',
                        '_kenburnsScaleContainer.scale.y',
                    ],
                });
                break;
            }

            case Config.NONE_BACKGROUND_ANIMATION:
                break;

            default:
                throw Error(`Invalid key burn option ${this.#kenburn}`);
        }
    }

    /**
     * Return the current time in seconds
     */
    get currentTime(): number {
        if (this.isVideo && this.isSourceValid) {
            return (this.textureSourceDomElement as HTMLVideoElement).currentTime;
        }
        return 0;
    }

    timeRelativeTo(time: number): number {
        return time;
    }

    /**
     * directly save the private variable because public getter can throw error
     * if media is linked to scene group.
     */
    _playbackRegionsCustomSave() {
        return this.#playbackRegions;
    }

    _playbackRegionsCustomLoad(value: PlaybackRegion[]) {
        this.#playbackRegions = value;
        this.#audioAsset._playbackRegionsCustomLoad(value);
        this.requiresRedraw = true;
    }

    get playbackRegions(): Readonly<Readonly<PlaybackRegion>[]> {
        if (this.linkToSceneGroup) {
            throw new Error(
                'Do not access playback regions on a linked media asset. Use sceneGroup.mediaAsset instead.',
            );
        }

        return this.#playbackRegions;
    }

    cut(start: number, end: number) {
        if (!this.isVideo) {
            throw new Error('Cannot cut a non-video media asset');
        }
        if (this.linkToSceneGroup) {
            throw new Error('Do not cut linked media asset directly.');
        }
        const sourceDataDuration = this.sourceDuration;
        if (sourceDataDuration === null) {
            throw new Error('Cannot cut a media asset without source data duration');
        }
        this.#playbackRegions = PlaybackRegions.cut(
            start,
            end,
            sourceDataDuration,
            this.#playbackRegions,
        );
        this.requiresRedraw = true;
    }

    uncut(start: number, end: number) {
        if (!this.isVideo) {
            throw new Error('Cannot uncut a non-video media asset');
        }
        if (this.linkToSceneGroup) {
            throw new Error('Do not uncut linked media asset directly.');
        }
        const sourceDataDuration = this.sourceDuration;
        if (sourceDataDuration === null) {
            throw new Error('Cannot uncut a media asset without source data duration');
        }
        this.#playbackRegions = PlaybackRegions.uncut(
            start,
            end,
            sourceDataDuration,
            this.#playbackRegions,
        );
        this.requiresRedraw = true;
    }

    override _seek(progress: number, seekMode: SeekMode): void {
        super._seek(progress, seekMode);
        if (this.activeResource instanceof WebpResource) {
            this.activeResource.seek(this.luminaryParent!.currentTime);
        }
    }

    /**
     * Buffer data at the given time and resolve the moment we have enough data to enable the given mode (single or play).
     * Single mode is mainly being use when rendering images while play is used to enable playback.
     */
    override async _bufferAtTime(time: number, bufferingMode: BufferingMode): Promise<void> {
        if (
            typeof this.fetchResources !== 'boolean' &&
            this.fetchResources?.media === FETCH_ON_BUFFER
        ) {
            // At this point, since we requested buffering, we now should load media for this asset
            this._loadSourceWithNetworkManager(true);
        }

        try {
            await this.onSourceLoaded();

            // check for cases where buffering isn't possible
            if (
                (!this.isVideo && !WebpResource.isWebp(this.#sourceUrl)) ||
                this.failedToLoad ||
                !this._shouldLoad ||
                !this.visible
            ) {
                return;
            }

            // if the time is for before this asset, just buffer at zero
            time = Math.max(time, 0);

            if (this.activeResource instanceof WebpResource) {
                await this.activeResource.buffer(time);
                return;
            }
            await this.#bufferAtTime(time, bufferingMode);
            await this.#audioAsset._bufferAtTime(time, bufferingMode);
        } catch (e) {
            if (e instanceof BufferingAbortedError) {
                // Don't propagate an error, but rather just resolve
                return;
            }
            throw e;
        }
    }

    async onSourceLoaded(): Promise<void> {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.onSourceLoaded();
        }
        if (this.#isSourceLoaded || this.#failed || this.destroyed) {
            return;
        }
        return new Promise((resolve, reject) => {
            this.once('onLoaded', resolve);
            this.once('onLoadAttemptFailed', (error: Error) => {
                reject(
                    new FailedToLoadError('MediaAsset failed to load.', {
                        error,
                        sourceData: this.sourceData,
                        readyState:
                            this.isVideo && this.isLinkedToMediaAsset
                                ? (this.textureSourceDomElement as HTMLVideoElement)?.readyState
                                : null,
                    }),
                );
            });
            this.#abortBuffering = reject;
        });
    }

    /**
     * When there are multiple playback regions, to ensure continuous playback, we need to pre-seek the video elements
     * for each playback region. This function will seek the regions depending on bufferingMode.
     *
     * BufferingMode.ResolveWhenCanPlay: will seek all regions and wait for all of them to be ready to play.
     * BufferingMode.PrepareForPlayback: will seek all regions but won't wait for them to be ready to play.
     * BufferingMode.ResolveWhenSingleFrame: will seek the first region that playback time is within and wait for it
     * to be ready to play, but won't wait for the rest of the regions.
     */
    async _resetPlaybackRegions(playbackTime: number, bufferingMode: BufferingMode) {
        // we don't need to pre-seek if there aren't multiple playback regions
        const sourceDataDuration = this.sourceDuration;
        if (
            !this.isVideo ||
            sourceDataDuration === null ||
            this.#playbackRegions.length <= 1 ||
            !this.isLoaded ||
            this.linkToSceneGroup
        ) {
            return;
        }

        // idx of the playback region playbackTime is within
        const assetTime = PlaybackRegions.playbackTimeToAssetTime(
            this.#playbackRegions,
            sourceDataDuration,
            playbackTime,
        );
        const activePlaybackRegionIdx = this.#playbackRegions.findIndex(
            ([start, end]) => assetTime >= start && assetTime < end,
        );
        let idx = 0;
        const seekPromises: Promise<void>[] = [];
        let shouldWaitForSeek;

        for (const [startTimeMS] of this.#playbackRegions) {
            const startTime = startTimeMS / 1000;
            let shouldPerformSeek;
            if (bufferingMode === BufferingMode.ResolveWhenSingleFrame) {
                // let bufferAtTime handle the seek
                shouldPerformSeek = false;
                shouldWaitForSeek = false;
            } else if (bufferingMode === BufferingMode.ResolveWhenCanPlay) {
                // bufferAtTime will seek the active region, so avoid double seeking
                shouldPerformSeek = idx >= activePlaybackRegionIdx + 1;
                // can adjust how many future regions we want to wait for here
                shouldWaitForSeek = idx >= activePlaybackRegionIdx + 1;
            } else if (bufferingMode === BufferingMode.PrepareForPlayback) {
                shouldPerformSeek = true;
                shouldWaitForSeek = false;
            }

            if (shouldPerformSeek) {
                // closure variable for onSeeked
                const regionIdx = idx;
                const seekPromise = new Promise<void>((resolve) => {
                    // already seeked, resolve immediately
                    if (this.#playbackRegionSources[regionIdx].currentTime === startTime) {
                        resolve();
                    } else {
                        const onSeeked = () => {
                            this.#playbackRegionSources[regionIdx].removeEventListener(
                                'seeked',
                                onSeeked,
                            );
                            resolve();
                        };
                        this.#playbackRegionSources[regionIdx].addEventListener('seeked', onSeeked);
                        this.#playbackRegionSources[regionIdx].currentTime = startTime;
                    }
                });
                // later we evaluate whether we want for these seek operations to complete
                shouldWaitForSeek && seekPromises.push(seekPromise);
            }

            idx++;
        }

        await Promise.all(seekPromises);
    }

    async #bufferAtTime(time: number, bufferingMode: BufferingMode): Promise<void> {
        if (this.#sourceData === null) {
            return;
        }
        if (this.#linkedMediaAsset) {
            // Let linked media get buffer.
            return;
        }
        const sourceDataDuration = this.sourceDuration;
        if (sourceDataDuration === null) {
            return;
        }

        // we need to switch the playback region video before buffering to make sure we're buffering the correct video
        this.switchPlayback(
            PlaybackRegions.playbackTimeToAssetTime(
                this.#playbackRegions,
                sourceDataDuration,
                time,
            ),
        );
        // conditionally reset other playback regions to their start times
        await this._resetPlaybackRegions(time, bufferingMode);

        const waitForState =
            bufferingMode === BufferingMode.ResolveWhenCanPlay
                ? HTMLVideoElement.prototype.HAVE_ENOUGH_DATA
                : HTMLVideoElement.prototype.HAVE_CURRENT_DATA;
        const activeSource = <HTMLVideoElement>this.textureSourceDomElement;
        const isVideo = this.isVideo;

        const _isVideoSourceDoneBuffering = () => {
            // In firefox, the readyState changes asynchronously, so we also need
            // to check to see if it's seeking
            if (!isVideo) {
                return true;
            }
            return activeSource.readyState >= waitForState && activeSource.seeking === false;
        };

        return new Promise((resolve, reject) => {
            this.#abortBuffering = reject;
            const bufferingDoneListener = () => {
                if (!activeSource || this.destroyed) {
                    reject(new Error('Source is null'));
                    return;
                }
                // wait until the source tag is ready and is no longer seeking.
                if (_isVideoSourceDoneBuffering()) {
                    activeSource.removeEventListener('canplay', this._onSourceBufferingDone);
                    activeSource.removeEventListener('canplaythrough', this._onSourceBufferingDone);
                    activeSource.removeEventListener('seeked', this._onSourceBufferingDone);

                    this.#clearBufferingTimeoutFallback();
                    this.off('onBufferingDone', bufferingDoneListener);
                    resolve();
                }
            };
            this.on('onBufferingDone', bufferingDoneListener);

            if (!activeSource) {
                reject(new Error('textureSourceDomElement is null'));
                return;
            }
            activeSource.addEventListener('canplay', this._onSourceBufferingDone);
            activeSource.addEventListener('canplaythrough', this._onSourceBufferingDone);
            activeSource.addEventListener('seeked', this._onSourceBufferingDone);

            // when playback rate is below 0.075 we'll get a HTMLMediaElement
            // error -- this catches this error
            try {
                if (this.#playbackRegions.length > 1) {
                    const assetTime = PlaybackRegions.playbackTimeToAssetTime(
                        this.#playbackRegions,
                        sourceDataDuration,
                        time,
                    );
                    activeSource.currentTime = assetTime / 1000;
                } else {
                    // maintain looping on single playback region videos
                    const relTime = this.timeRelativeTo(time);
                    if (isNaN(relTime)) {
                        ErrorReporter.setContext('MediaAsset', { time, relTime });
                        throw new Error('Relative time is NaN');
                    }
                    activeSource.currentTime = relTime;
                }
            } catch (err) {
                console.warn(err);
                this.#clearBufferingTimeoutFallback();
                reject(err);
            }

            if (_isVideoSourceDoneBuffering()) {
                this.emit('onBufferingDone');
            } else {
                // maximum wait timeout fallback
                this.#clearBufferingTimeoutFallback();
                const seekWaitTime = this.#seekWaitTime;
                this.#onBufferingTimeoutFallback = setTimeout(() => {
                    activeSource.removeEventListener('canplay', this._onSourceBufferingDone);
                    activeSource.removeEventListener('canplaythrough', this._onSourceBufferingDone);
                    activeSource.removeEventListener('seeked', this._onSourceBufferingDone);

                    if (!_isVideoSourceDoneBuffering()) {
                        // if it's still seeking, let's listen for the seeked event and then trigger a redraw
                        if (activeSource.seeking) {
                            activeSource.addEventListener(
                                'seeked',
                                () => {
                                    this.requiresRedraw = true;
                                },
                                { once: true },
                            );
                        } else {
                            console.warn(
                                `Seek Timeout after ${seekWaitTime}ms = {readyState: ${activeSource.readyState}, seeking: ${activeSource.seeking}}, expecting {readyState: ${waitForState}, seeking: false}`,
                            );
                        }
                    }
                    resolve();
                }, seekWaitTime);
            }
        });
    }

    set brightness(value: number) {
        let filter = this.#getAdjustmentFilter();
        if (!filter) {
            if (value === AdjustmentFilter.NEUTRAL_BRIGHTNESS) {
                return;
            }
            filter = this.#getOrCreateAdjustmentFilter();
        }

        filter.brightness = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get brightness() {
        const filter = this.#getAdjustmentFilter();

        if (!filter) {
            return AdjustmentFilter.NEUTRAL_BRIGHTNESS;
        }

        return filter.brightness;
    }

    set contrast(value: number) {
        let filter = this.#getAdjustmentFilter();
        if (!filter) {
            if (value === AdjustmentFilter.NEUTRAL_CONTRAST) {
                return;
            }
            filter = this.#getOrCreateAdjustmentFilter();
        }
        filter.contrast = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get contrast() {
        const filter = this.#getAdjustmentFilter();

        if (!filter) {
            return AdjustmentFilter.NEUTRAL_CONTRAST;
        }

        return filter.contrast;
    }

    #getAdjustmentFilter(): AdjustmentFilter | undefined {
        return this.getFilterByName(MediaAsset.ADJUSTMENT_FILTER_NAME) as AdjustmentFilter;
    }

    #getOrCreateAdjustmentFilter(): AdjustmentFilter {
        let filter = this.#getAdjustmentFilter();

        if (!filter) {
            filter = this.addFilter(Config.ADJUSTMENT_FILTER, MediaAsset.ADJUSTMENT_FILTER_NAME);
        }

        return filter;
    }

    set hue(value: number) {
        let filter = this.#getHslAdjustmentFilter();
        if (!filter) {
            if (value === HslAdjustmentFilter.NEUTRAL_HUE) {
                return;
            }
            filter = this.#getOrCreateHslAdjustmentFilter();
        }
        filter.hue = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get hue() {
        const filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            return HslAdjustmentFilter.NEUTRAL_HUE;
        }

        return filter.hue;
    }

    set lightness(value: number) {
        let filter = this.#getHslAdjustmentFilter();
        if (!filter) {
            if (value === HslAdjustmentFilter.NEUTRAL_LIGHTNESS) {
                return;
            }
            filter = this.#getOrCreateHslAdjustmentFilter();
        }
        filter.lightness = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get lightness() {
        const filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            return HslAdjustmentFilter.NEUTRAL_LIGHTNESS;
        }

        return filter.lightness;
    }

    set saturation(value: number) {
        let filter = this.#getHslAdjustmentFilter();
        if (!filter) {
            if (value === HslAdjustmentFilter.NEUTRAL_SATURATION) {
                return;
            }
            filter = this.#getOrCreateHslAdjustmentFilter();
        }
        filter.saturation = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get saturation() {
        const filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            return HslAdjustmentFilter.NEUTRAL_SATURATION;
        }

        return filter.saturation;
    }

    #getHslAdjustmentFilter(): HslAdjustmentFilter | undefined {
        return this.getFilterByName(MediaAsset.HSL_ADJUSTMENT_FILTER_NAME) as HslAdjustmentFilter;
    }

    #getOrCreateHslAdjustmentFilter(): HslAdjustmentFilter {
        let filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            filter = this.addFilter(
                Config.HSL_ADJUSTMENT_FILTER,
                MediaAsset.HSL_ADJUSTMENT_FILTER_NAME,
            );
        }

        return filter;
    }

    set blur(value: number) {
        let filter = this.getFilterByName(MediaAsset.BLUR_FILTER_NAME) as BlurFilter;

        if (!filter) {
            if (value === BlurFilter.NEUTRAL_BLUR) {
                return;
            }
            filter = this.addFilter(Config.BLUR_FILTER, MediaAsset.BLUR_FILTER_NAME);
        }

        filter.blur = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get blur(): number {
        const filter = this.getFilterByName(MediaAsset.BLUR_FILTER_NAME) as BlurFilter;

        if (!filter) {
            return BlurFilter.NEUTRAL_BLUR;
        }

        return filter.blur;
    }

    set noise(value: number) {
        let filter = this.#getOldFilmFilter();
        if (!filter) {
            if (value === OldFilmFilter.NEUTRAL_NOISE) {
                return;
            }
            filter = this.#getOrCreateOldFilmFilter();
        }
        filter.noise = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get noise() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return OldFilmFilter.NEUTRAL_NOISE;
        }

        return filter.noise;
    }

    set noiseSize(value: number) {
        let filter = this.#getOldFilmFilter();
        if (!filter) {
            if (value === OldFilmFilter.NEUTRAL_NOISE_SIZE) {
                return;
            }
            filter = this.#getOrCreateOldFilmFilter();
        }
        filter.noiseSize = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get noiseSize() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return OldFilmFilter.NEUTRAL_NOISE_SIZE;
        }

        return filter.noiseSize;
    }

    set vignetting(value: number) {
        let filter = this.#getOldFilmFilter();
        if (!filter) {
            if (value === OldFilmFilter.NEUTRAL_VIGNETTING) {
                return;
            }
            filter = this.#getOrCreateOldFilmFilter();
        }
        filter.vignetting = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get vignetting() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return OldFilmFilter.NEUTRAL_VIGNETTING;
        }

        return filter.vignetting;
    }

    set vignettingAlpha(value: number) {
        let filter = this.#getOldFilmFilter();
        if (!filter) {
            if (value === OldFilmFilter.NEUTRAL_VIGNETTING_ALPHA) {
                return;
            }
            filter = this.#getOrCreateOldFilmFilter();
        }

        filter.vignettingAlpha = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get vignettingAlpha() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return OldFilmFilter.NEUTRAL_VIGNETTING_ALPHA;
        }

        return filter.vignettingAlpha;
    }

    set vignettingBlur(value: number) {
        let filter = this.#getOldFilmFilter();
        if (!filter) {
            if (value === OldFilmFilter.NEUTRAL_VIGNETTING_BLUR) {
                return;
            }
            filter = this.#getOrCreateOldFilmFilter();
        }
        filter.vignettingBlur = value;
        this.removeFilterIfNeutral(filter.name);
    }

    get vignettingBlur() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return OldFilmFilter.NEUTRAL_VIGNETTING_BLUR;
        }

        return filter.vignettingBlur;
    }

    #getOldFilmFilter(): OldFilmFilter | undefined {
        return this.getFilterByName(MediaAsset.OLD_FILM_FILTER_NAME) as OldFilmFilter;
    }

    #getOrCreateOldFilmFilter(): OldFilmFilter {
        let filter = this.#getOldFilmFilter();

        if (!filter) {
            filter = this.addFilter(Config.OLD_FILM_FILTER, MediaAsset.OLD_FILM_FILTER_NAME);
            filter.seed = Math.random();
        }

        return filter;
    }

    set enableGreyscale(value: boolean) {
        const filter = this.getFilterByName(MediaAsset.GREYSCALE_FILTER_NAME);
        if (value && !filter) {
            this.addFilter(Config.GREYSCALE_FILTER, MediaAsset.GREYSCALE_FILTER_NAME);
        } else if (!value && filter) {
            this.removeFilter(MediaAsset.GREYSCALE_FILTER_NAME);
        }
    }

    get enableGreyscale(): boolean {
        return this.getFilterByName(MediaAsset.GREYSCALE_FILTER_NAME) !== undefined;
    }

    /**
     * Exposes the filters which are not managed by luminary automatically. Some filters should only be used internally,
     * to provide functionality for MediaAsset, so outside of luminary we want to filter those out.
     */
    get externalFilters() {
        return this.getFilters().filter(
            (f) =>
                f.name !== MediaAsset.ADJUSTMENT_FILTER_NAME &&
                f.name !== MediaAsset.BLUR_FILTER_NAME &&
                f.name !== MediaAsset.GREYSCALE_FILTER_NAME &&
                f.name !== MediaAsset.HSL_ADJUSTMENT_FILTER_NAME &&
                f.name !== MediaAsset.OLD_FILM_FILTER_NAME &&
                f.name !== MediaAsset.COLOR_GRADING_FILTER_NAME,
        );
    }

    override get hasAudio(): boolean | null {
        if (super.hasAudio) {
            return true;
        }
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.hasAudio;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.has_audio ?? null;
    }

    get hasVoiceover(): boolean | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.hasVoiceover;
        }
        if (this.#sourceData === null) {
            return null;
        }
        return this.#sourceData.has_voiceover ?? null;
    }

    get colorGradingManager(): ColorGradingManager {
        return this.#colorGradingManager;
    }

    get transformMatrix(): TransformMatrix {
        return this.#transformMatrix;
    }

    set scaleX(value: number) {
        this.#transformMatrix.scaleX = value;
    }

    get scaleX() {
        return this.#transformMatrix.scaleX;
    }

    set scaleY(value: number) {
        this.#transformMatrix.scaleY = value;
    }

    get scaleY() {
        return this.#transformMatrix.scaleY;
    }

    // TODO would be nice to refactor the API to use mediaAsset.transform.x/yTranslation
    set xTranslation(value) {
        this.#transformMatrix.xTranslation = value;
    }

    get xTranslation() {
        return this.#transformMatrix.xTranslation;
    }

    set yTranslation(value) {
        this.#transformMatrix.yTranslation = value;
    }

    get yTranslation() {
        return this.#transformMatrix.yTranslation;
    }

    get usesInvisibleURL() {
        return (
            this.thumbnailUrl === Config.INVISIBLE_IMAGE &&
            this.previewUrl === Config.INVISIBLE_IMAGE &&
            this.renderUrl === Config.INVISIBLE_IMAGE
        );
    }

    override get rotation() {
        return this.#scaleRotateContainer.rotation;
    }

    override set rotation(value: number) {
        this.#scaleRotateContainer.rotation = value;
    }

    override get propertiesToSerialize() {
        const properties = [
            'mask',
            'fillMode',
            'kenburn',
            'maxHeight',
            'maxWidth',
            'scaleMultiplier',
            'transformMatrix',
            'createdFrom',
        ];
        return super.propertiesToSerialize
            .concat(getAttributesToSerialize(this))
            .concat(properties);
    }

    /**
     * List of element properties that can be edited by users and would need to be preserved
     * between aspect ratio changes.
     */
    override get editableProperties() {
        return super.editableProperties.concat([
            'sourceData',
            'playbackRegions',
            'gradingOverride',
            'linkToSceneGroup',
        ]);
    }

    override getCreationContainersAtLevelForType(
        level: DynamicStylingCreationLevel,
        type = CreationType.Animation,
    ): PIXI.Container[] {
        assertInEnum(type, CreationType);
        switch (level) {
            case DynamicStylingCreationLevel.Object:
                // The container used for animation is below the container where we add dynamic graphics to. This means
                // that animations are not applied to the dynamic graphics and let us only control the media asset itself.
                // This is important for masks -- if we want to reveal the media by moving it out from under a mask, we
                // don't want the mask to move with it. Animations for dynamic graphics can be set up on the graphic itself.
                return type === CreationType.Animation
                    ? [this.#dynamicAnimationContainer]
                    : [this.#scaleRotateContainer];
            default:
                console.warn(`Unsupported container of type ${level}`);
                return [];
        }
    }

    /**
     * get data object containing the base 64 string of the media asset including format and dimensions. This is handy
     * when the media asset is a linked media asset and thus doesn't have a URL to fetch the data directly from for
     * display.
     */
    async getBase64Data(): Promise<MediaAssetBase64Data> {
        const renderer = this.root.renderer;
        if (!renderer) {
            throw new Error('Cannot get base64 data without a renderer');
        }
        // set the renderer resolution to 1 to get the correct base64 data
        const previousResolution = renderer.resolution;
        renderer.resolution = 1;

        const format = 'image/png';
        if (this.#linkedMediaAsset) {
            this.#linkedMediaAsset.updateForRender();
        }
        this.updateForRender();
        const asset = this.#linkedMediaAsset || this;
        const width = asset.originalResourceResolution.width;
        const height = asset.originalResourceResolution.height;
        const base64 = await renderer.extract.base64(this.#sprite, format, 1);
        if (this.destroyed) {
            throw new Error('media asset is destroyed');
        }
        renderer.resolution = previousResolution;
        this._reusableTextures.detachTextures();

        return new MediaAssetBase64Data({
            base64,
            width,
            height,
            format,
        });
    }

    static #calculateAspectRatio(media: { width: number; height: number }): AspectRatio {
        if (media.width > media.height) {
            return AspectRatio.LANDSCAPE;
        }

        if (media.width < media.height) {
            return AspectRatio.VERTICAL;
        }

        return AspectRatio.SQUARE;
    }

    /**
     * get the aspect ratio for SourceData
     */
    get sourceAspectRatio(): AspectRatio {
        return MediaAsset.#calculateAspectRatio(this.sourceData);
    }

    /**
     * get the aspect ratio for MediaTarget
     */
    get targetAspectRatio(): AspectRatio {
        return MediaAsset.#calculateAspectRatio(this.mediaTargetResolution);
    }
}
